<template>
  <div class="ui grid" style="width: 100%">
    <div class="row">
      <h1 class="ui huge header">Schools</h1>
    </div>
    <div class="ui divider"></div>
    <div class="row">
      <sui-button content="Import school list" positive icon="plus" v-on:click="showImportModal">
      </sui-button>
      <sui-button content="Add school" positive icon="plus" v-on:click="showSchoolModal">
      </sui-button>
    </div>
    <div class="row right floated">
      <div class="right floated right aligned size wide column">
        <span>Search for school: <input v-model="filter" type="text" /></span><br />
        <label>Include archived: <input v-model="includeArchived" type="checkbox" /></label><br />
        <label>Archived only: <input v-model="archivedOnly" :disabled="!includeArchived" type="checkbox" /></label>
      </div>
    </div>
    <div class="row">
      <school-table v-bind:schools="schools.schools" v-bind:actions="schoolActions" v-on:rowAction="rowAction" />
    </div>
    <!-- eslint-disable-next-line max-len -->
    <sui-progress v-if="importing" indicating :percent="importPercentage" :label="importLabel" style="width: 100%" />
    <portal to="modal-destination">
      <sui-modal v-model="importModalOpen">
        <sui-modal-header>Import school list</sui-modal-header>
        <sui-modal-content class="scrolling">
          <school-import v-on:imported="importSchools" ref="importSchools" />
        </sui-modal-content>
        <sui-modal-actions>
          <sui-button negative v-on:click="hideImportModal">
            Cancel
          </sui-button>
        </sui-modal-actions>
      </sui-modal>
      <sui-modal v-model="schoolModalOpen">
        <sui-modal-header>School details</sui-modal-header>
        <sui-modal-content class="scrolling">
          <school-form v-bind:school="school" v-bind:error="schoolError" v-on:saved="schoolSaved" create="true"
            ref="schoolForm" />
        </sui-modal-content>
        <sui-modal-actions>
          <sui-button negative v-on:click="hideSchoolModal">
            Cancel
          </sui-button>
        </sui-modal-actions>
      </sui-modal>
    </portal>
    <div class="ui container center aligned">
      <sui-button v-if="hasPreviousPage" v-on:click="previousPage" content="Previous" />
      &nbsp;<span>Page: {{ this.paging.page }} of {{ totalPages }}</span>&nbsp;
      <sui-button v-if="hasNextPage" v-on:click="nextPage" content="Next" />
    </div>
  </div>
</template>

<script>
import SchoolTable from '@/components/schools/SchoolTable';
import SchoolImport from '@/components/schools/SchoolImport';
import SchoolForm from '@/components/schools/SchoolForm';
import { mapGetters } from 'vuex';

export default {
  name: 'SchoolSelection',
  components: { SchoolTable, SchoolImport, SchoolForm },
  computed: {
    hasNextPage() {
      return this.paging.page * this.paging.pageSize < this.schools.schoolCount;
    },
    hasPreviousPage() {
      return this.paging.page > 1;
    },
    totalPages() {
      return Math.ceil(this.schools.schoolCount / this.paging.pageSize);
    },
    ...mapGetters({
      schools: 'pagedSchools',
      paging: 'schoolsPaging',
    }),
  },
  data() {
    return {
      schoolActions: ['Details'],
      importModalOpen: false,
      schoolModalOpen: false,
      schoolError: null,
      importing: false,
      importPercentage: 0,
      importLabel: '',
      includeArchived: false,
      archivedOnly: false,
      filter: '',
      currentSchool: {
        data: null,
      },
      school: null,
    };
  },
  watch: {
    filter(val) {
      this.$store.dispatch('getPagedSchools', {
        ...this.paging,
        filter: val,
      });
    },
    includeArchived(val) {
      if (!val) {
        this.archivedOnly = false;
      }
      this.$store.dispatch('getPagedSchools', {
        ...this.paging,
        includeArchived: val,
        archivedOnly: this.archivedOnly,
      });
    },
    archivedOnly(val) {
      this.$store.dispatch('getPagedSchools', {
        ...this.paging,
        includeArchived: this.includeArchived,
        archivedOnly: val,
      });
    },
  },
  methods: {
    rowAction(action) {
      switch (action.action) {
        case 'Details':
          this.$router.push({
            name: '/school/details',
            params: { schoolId: action.school.id },
          });
          break;
        case 'Teachers':
          this.$router.push({
            name: '/school/teachers',
            params: { school: action.school },
          });
          break;
        default:
          break;
      }
    },
    nextPage() {
      this.$store.dispatch('getPagedSchools', {
        ...this.paging,
        page: this.paging.page + 1,
      });
    },
    previousPage() {
      this.$store.dispatch('getPagedSchools', {
        ...this.paging,
        page: this.paging.page - 1,
      });
    },
    showImportModal() {
      this.importModalOpen = true;
    },
    hideImportModal() {
      this.importModalOpen = false;
    },
    async importSchools() {
      this.importing = true;
      this.importModalOpen = false;
      const totalImportCount = this.$refs.importSchools.schools.length;
      for (let i = 0; i < this.$refs.importSchools.schools.length; i += 1) {
        this.importPercentage = ((i + 1) / totalImportCount) * 100;
        this.importLabel = `Importing school ${i + 1} of ${totalImportCount}`;
        /* eslint-disable-next-line no-await-in-loop */
        await this.$store.dispatch(
          'createSchool',
          this.$refs.importSchools.schools[i],
        );
      }
      this.importLabel = 'Import finished';
      this.$store.dispatch('getPagedSchools', this.paging);
      this.$refs.importSchools.schools = [];
    },
    schoolSaved() {
      this.school.totalStudentCount = parseInt(
        this.school.totalStudentCount,
        10,
      );
      this.$store
        .dispatch('createSchool', this.school)
        .then(() => {
          this.hideSchoolModal();
        })
        .catch(() => { });
    },
    showSchoolModal() {
      this.school = {
        totalStudentCount: 100,
        availableLanguages: ['en', 'ga'],
        defaultLanguage: 'en',
      };
      this.schoolError = null;
      this.$refs.schoolForm.reset();
      this.schoolModalOpen = true;
    },
    hideSchoolModal() {
      this.schoolModalOpen = false;
      this.$store.dispatch('getPagedSchools', { ...this.paging, filter: '' });
    },
  },
  created() {
    this.filter = this.paging.filter;
    this.includeArchived = this.paging.includeArchived;
    this.archivedOnly = this.paging.archivedOnly;
    this.$store.dispatch('getPagedSchools', { ...this.paging, filter: '' });
  },
};
</script>



// WEBPACK FOOTER //
// src/components/schools/SchoolSelection.vue